<template>
  <div>
    <Modal
      :width="400"
      :value="defaultQuestionModalOpen"
      ctaText="Add"
      actions-border
      overlayColor="#ffffff"
      overlayOpacity="0.83"
      @cancel="defaultQuestionModalOpen = false"
      title="Add default question"
      @confirm="confirmDefaultQuestionModal">
      <template #default>
        <label class="default-question__label"> {{ $t("shortcodes.live_chat_auto.default_question") }} </label>
        <v-select
          v-model="defaultQuestionsValue"
          class="default-question__select"
          hide-details
          :items="defaultQuestionsOption"
          :menu-props="{
            offsetOverflow: true,
            offsetY: true,
          }"
          placeholder="Default question">
        </v-select>
      </template>
    </Modal>
    <div class="zoom-actions">
      <v-btn @click="zoomIn">+</v-btn>
      <v-btn class="ml-2" @click="zoomOut">-</v-btn>
      <v-btn class="ml-2" @click="reset">
        <v-icon class="mr-1" color="icon" small>mdi-restart</v-icon>
      </v-btn>
    </div>

    <!---JSON code-->
    <div class="json-action">
      <v-btn @click="collapseJsonPreview">
        <v-icon> mdi-code-json</v-icon>
      </v-btn>
    </div>

    <div v-if="displayJsonPreview" class="json-preview">
      <div class="json-preview__header">
        <div v-if="jsonFormatError" class="d-flex align-center mb-3">
          <span class="json-preview__format-error">{{ $t("shortcodes.live_chat_auto.invalid_json_format") }}</span>
          <v-tooltip top>
            <template #activator="{ on }">
              <v-btn v-on="on" @click="undoJson" class="ml-3">
                <v-icon>mdi-undo</v-icon>
              </v-btn>
            </template>
            <p>{{ $t("shortcodes.live_chat_auto.undo_to_previous") }}</p>
          </v-tooltip>
        </div>
      </div>
      <code-input
        :class="{ 'code-error': jsonFormatError }"
        :key="jsonInputKey"
        v-model="liveChatDataJsonFormat"
        :title="''"></code-input>
    </div>
    <!---JSON code END -->

    <div class="panzoom-parent">
      <div ref="panzoom">
        <!--Chatbot settings -->
        <div class="d-flex justify-center">
          <div class="chatbot-data">
            <div class="title mb-2">{{ $t("shortcodes.live_chat_auto.chatbot_settings") }}</div>
            <v-text-field
              v-model="liveChatData.botName"
              persistent-placeholder
              :label="'Chatbot name'"
              :placeholder="'Mary'"></v-text-field>
            <VaMediaObjectFileInput
              label="Chatbot avatar"
              class="mb-4"
              :item-id="liveChatData.botAvatar.imageID"
              @update:photo="updateAvatar($event)" />
            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'Action items background color'"
              v-model="liveChatData.actionItemBackgroundColor"></VaColorPicker>

            <VaColorPicker
              class="mb-4 panzoom-ignore"
              :title="'User message background color'"
              v-model="liveChatData.userMessageBackgroundColor"></VaColorPicker>
            <div class="d-flex justify-center">
              <v-btn :color="'primary'" class="mr-2" v-if="displayAddMessageBtn" @click="addMessage"
                >{{ $t("shortcodes.live_chat_auto.add_message") }}
              </v-btn>
              <v-btn class="mr-2" v-if="displayAddMessageBtn" @click="handleDefaultQuestionModal">
                {{ $t("shortcodes.live_chat_auto.add_default_question") }}
              </v-btn>
            </div>
          </div>
        </div>
        <!--Chatbot settings END-->
        <!--Messages -->
        <div class="message-tree">
          <LiveChatAutoTree :key="liveChatTreeKey" :message-tree="liveChatData.messages"></LiveChatAutoTree>
        </div>
        <!--Messages END -->
      </div>
    </div>
  </div>
</template>

<script>
  // Components
  // import LiveChatTree from "@/components/shortcodes/livechat/LiveChatTree.vue";
  import LiveChatAutoTree from "@/components/shortcodes/livechatAuto/LiveChatAutoTree.vue";
  import Panzoom from "@panzoom/panzoom";
  import CodeInput from "@/components/custom/CodeInput";
  import VaColorPicker from "@/components/custom/VaColorPicker";
  import Modal from "@/components/custom/Modal.vue";
  // Helpers
  import debounce from "lodash.debounce";
  import cloneDeep from "lodash/cloneDeep";
  // Constants
  import { DEFAULT_QUESTION, MESSAGE } from "@/components/shortcodes/livechat/constant";
  import definedAbility from "@/ability";
  import { LIVE_CHAT_DEFAULT_QUESTIONS } from "@/constant";

  export default {
    name: "LiveChatDialog",
    components: { LiveChatAutoTree, CodeInput, VaColorPicker, Modal },
    data() {
      return {
        ability: definedAbility(),
        panzoomInstance: null,
        liveChatData: {
          botName: "Mary",
          botAvatar: {
            imageID: "",
            imageURL: "",
          },
          actionItemBackgroundColor: "#007bee",
          userMessageBackgroundColor: "#007bee",
          messages: [],
        },
        /**
         * When editing JSON input (code-input), user might make the JSON inside of the code-input invalid;
         * jsonFormatError is true if JSON format inside of the code-input is incorrect (not parseable using JSON.parse)
         */
        displayJsonPreview: false,
        jsonFormatError: false,
        paramData: null,
        jsonInputKey: 0,
        liveChatTreeKey: 0,
        defaultQuestionModalOpen: false,
        defaultQuestionsValue: "zip",
        defaultQuestionsOption: LIVE_CHAT_DEFAULT_QUESTIONS,
      };
    },
    computed: {
      displayAddMessageBtn() {
        return this.liveChatData.messages && this.liveChatData.messages.length === 0;
      },
      liveChatDataJsonFormat: {
        get() {
          const displayData = this.liveChatData;

          return JSON.stringify(displayData, null, " ");
        },
        set(value) {
          let parsedJson;
          try {
            this.jsonFormatError = false;
            parsedJson = JSON.parse(value);
          } catch (e) {
            this.jsonFormatError = true;
          }

          if (!this.jsonFormatError) {
            /**
             * Re-render the tree when json code input is changed (Required so that tinymce inputs update correctly);
             * Might need optimization in case of large message tree
             */
            this.rerenderMessageTree();
            // Assign separately to keep reactivity (!!!)
            this.liveChatData.messages = parsedJson.messages;
            this.liveChatData.botName = parsedJson.botName;
            this.liveChatData.botAvatar = parsedJson.botAvatar;
            this.liveChatData.actionItemBackgroundColor = parsedJson.actionItemBackgroundColor;
            this.liveChatData.userMessageBackgroundColor = parsedJson.userMessageBackgroundColor;
          }
        },
      },
    },
    watch: {
      liveChatData: {
        handler(newValue) {
          this.updateValue(newValue);
        },
        deep: true,
      },
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    mounted() {
      this.panzoomInstance = Panzoom(this.$refs.panzoom, {
        canvas: true,
        /**
         * Ignores all panzoom events on elements with provided class;
         * This is required because sometimes we use mouse click and drag to select things (text in the input for example)
         * This prevents panzoom from moving the view when trying to select text
         */
        excludeClass: "panzoom-ignore",
        /**
         * By default, panzoom will call event.preventDefault() and event.stopPropagation() on every pan event.
         * This prevents colorpicker from being closed on outside click.
         * Override the default handleStartEvent function to solve this.
         */
        handleStartEvent: () => {},
      });

      // Set appropriate height for panzoom wrapper. Set ti to window innerheight
      this.$refs.panzoom && (this.$refs.panzoom.style.height = `${window.innerHeight - 24}px`);

      const parent = this.$refs.panzoom.parentElement;

      parent.addEventListener("wheel", event => {
        if (!event.shiftKey) {
          this.panzoomInstance.pan(-event.deltaX, -event.deltaY, { relative: true });

          return;
        }
        this.panzoomInstance.zoomWithWheel(event);
      });
    },
    methods: {
      addMessage() {
        const message = cloneDeep(MESSAGE);
        this.liveChatData.messages.push(message);
      },
      handleDefaultQuestionModal() {
        this.defaultQuestionModalOpen = true;
      },
      confirmDefaultQuestionModal() {
        this.defaultQuestionModalOpen = false;

        const defaultQuestion = cloneDeep(DEFAULT_QUESTION);
        defaultQuestion.defaultQuestionType = this.defaultQuestionsValue;
        defaultQuestion.content = `<strong>${
          this.defaultQuestionsOption.find(option => option.value === this.defaultQuestionsValue).text
        }</strong>`;

        this.liveChatData.messages.push(defaultQuestion);
      },
      rerenderMessageTree: debounce(function () {
        this.liveChatTreeKey++;
      }, 1000),
      reset() {
        this.panzoomInstance.reset();
      },
      zoomIn() {
        this.panzoomInstance.zoomIn();
      },
      zoomOut() {
        this.panzoomInstance.zoomOut();
      },
      updateAvatar(value) {
        if (value) {
          this.liveChatData.botAvatar.imageID = value["@id"];

          if (this.ability.can("upload", "media object to inulti server")) {
            // Keep relative image URL, instead of appending inulti server URL. This way we discard inulti reference from generated pages;
            this.liveChatData.botAvatar.imageURL = value.contentUrl;
          } else {
            this.liveChatData.botAvatar.imageURL = value.contentUrl;
          }
        } else {
          this.liveChatData.botAvatar.imageID = "";
          this.liveChatData.botAvatar.imageURL = "";
        }
      },
      collapseJsonPreview() {
        this.displayJsonPreview = !this.displayJsonPreview;
      },
      /**
       * Updates the JSON input to previous valid state.
       */
      undoJson() {
        const displayData = this.liveChatData;
        this.liveChatDataJsonFormat = JSON.stringify(displayData, null, " ");
        this.jsonInputKey++;
      },
      updateValue(val) {
        window.parent.postMessage(
          {
            mceAction: "updateLiveChatData",
            data: val,
          },
          origin,
        );
      },
      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === "sendData") {
            const { data } = message.data;

            this.liveChatData = data;
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: "requestData",
          },
          origin,
        );
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import "@core/preset/preset/typography/index.scss";
  @import "@core/preset/preset/functions.scss";

  .zoom-actions {
    position: absolute;
    top: 20px;
    left: 20px;
    z-index: 100;
  }

  .json-action {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 102;
  }

  .title {
    color: get-color("subtitle");
    @include heading-4();
  }

  .chatbot-data {
    border: 1px dashed get-color("primary");
    border-radius: 5px;
    background: get-color("element-bg-100");
    padding: 5px 10px;
    max-width: 600px;
    min-width: 600px;
  }

  .message-tree {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);

    .message-item {
      border: 1px dashed get-color("primary");
      border-radius: 5px;
      background: get-color("element-bg-100");
      padding: 5px 10px;

      &__settings-wrapper {
        display: flex;
        align-items: center;
      }

      .message-item__settings {
        &--delay {
          max-width: 100px;
        }

        &--human {
          margin-left: 10px;
        }

        &--no-icon {
          margin-left: 10px;
        }
      }
    }
  }

  .json-preview {
    position: absolute;
    top: 0;
    left: 5px;
    right: 5px;
    bottom: 5px;
    z-index: 101;
    background-color: get-color("element-bg-200");

    &__header {
      display: flex;
      background: get-color("element-bg-200");
      padding: 70px 20px 0;

      &-title {
        color: get-color("subtitle");
        @include text-body-1-bold();
      }
    }

    &__format-error {
      color: get-color("error");
      @include text-body-1-bold();
    }
  }

  .json-error {
    background-color: get-color("error");
  }

  .code-error {
    border: 3px dashed get-color("error") !important;
  }
</style>
